import { get, findIndex, set, isEmpty } from 'lodash';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@repo/ui';
import { AttorneyResponseStatus } from '@repo/types';
import { AppealStatus } from '@repo/types';
import { queryClient } from '@xtnd/web/providers/QueryClientProvider';
import { useQueryKeyStore } from '@xtnd/web/shared/stores';
import { invoiceApi, InvoiceSearchParams, BulkAssignAppealsClerkPayload } from '../api';
import {
  AttorneyResponsePatchPayload,
  AttorneyResponsePayload,
  DelegateUpdatePayload,
  MatterInvoice,
} from '../types';
import { getApiErrorMessage } from '../utils/apiErrorHandler';

/**
 * Query hook for fetching a single invoice by its ID.
 *
 * @param invoiceId - The ID of the invoice to fetch.
 * @param options - Optional configuration options.
 * @returns A React Query result object containing the invoice data and query status.
 */
export const useGetInvoice = (invoiceId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['invoices', invoiceId],
    queryFn: () => invoiceApi.getInvoice(invoiceId),
    enabled: options?.enabled ?? true,
    staleTime: 0,
    gcTime: 0,
    retry: 1,
  });
};

/**
 * React Query hook for fetching the attorney response for a given invoice and appeal.
 *
 * @param invoiceId - The unique ID of the invoice.
 * @param appealId - The unique ID of the appeal associated with the invoice.
 * @param options - Optional configuration for the query:
 *   - enabled: Whether the query should be active (default: true).
 *
 * @returns A React Query result object containing the attorney response data and query status.
 *
 * @example
 * const { data, isLoading } = useGetAttorneyResponse(invoiceId, appealId);
 */
export function useGetAttorneyResponse(
  invoiceId: string,
  appealId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['attorneyResponse', invoiceId, appealId],
    queryFn: () => invoiceApi.getAttorneyResponse(invoiceId, appealId),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * React Query hook for submitting an attorney response for a given invoice and appeal.
 *
 * @param invoiceId - The unique identifier of the invoice.
 * @param appealId - The unique identifier of the appeal.
 * @param payload - The attorney response payload.
 * @returns A React Query result object containing the attorney response data and query status.
 */
export function useSubmitAttorneyResponse() {
  return useMutation({
    mutationFn: ({
      invoiceId,
      appealId,
      payload,
    }: {
      invoiceId: string;
      appealId: string;
      payload: AttorneyResponsePayload;
    }) => invoiceApi.submitAttorneyResponse(invoiceId, appealId, payload),
    onSuccess: _response => {
      const invoiceId = get(_response, 'invoiceId');
      const appealId = get(_response, 'appeal.appealId');
      queryClient.invalidateQueries({
        queryKey: ['attorneyResponse', invoiceId, appealId],
      });
      showToast.success('Attorney response submitted successfully!');
    },
    onError: (error: any) => {
      showToast.error(
        getApiErrorMessage(error, 'Failed to submit attorney response. Please try again.')
      );
    },
  });
}

/**
 * React Query hook for updating an attorney response for a specific invoice, appeal, and timekeeper.
 *
 * @returns A React Query mutation object for updating the attorney response.
 *
 * @example
 * const mutation = useUpdateAttorneyResponse();
 * mutation.mutate({
 *   invoiceId: '123',
 *   appealId: '456',
 *   timekeeperId: '789',
 *   payload: { ... } // AttorneyResponsePayload
 * });
 */
export function useUpdateAttorneyResponse() {
  return useMutation({
    mutationFn: ({
      invoiceId,
      appealId,
      timekeeperId,
      payload,
    }: {
      invoiceId: string;
      appealId: string;
      timekeeperId: string;
      payload: AttorneyResponsePayload;
    }) => invoiceApi.updateAttorneyResponse(invoiceId, appealId, timekeeperId, payload),
    onSuccess: _response => {
      const invoiceId = get(_response, 'invoiceId');
      const appealId = get(_response, 'appeal.appealId');
      queryClient.invalidateQueries({
        queryKey: ['attorneyResponse', invoiceId, appealId],
      });
      showToast.success('Attorney response submitted successfully!');
    },
    onError: (error: any) => {
      showToast.error(
        getApiErrorMessage(error, 'Failed to update attorney response. Please try again.')
      );
    },
  });
}

/**
 * React Query hook for patching (partially updating) an attorney response for a specific invoice, appeal, and timekeeper.
 *
 * Handles partial update requests to attorney responses, such as single-field or incremental updates.
 * On success, invalidates cached query data for the relevant attorney response and shows a success toast.
 * On error, displays an error notification with a detailed or generic message.
 *
 * @returns A React Query mutation object for patching the attorney response.
 *
 * @example
 * const mutation = usePatchAttorneyResponse();
 * mutation.mutate({
 *   invoiceId: '123',
 *   appealId: '456',
 *   timekeeperId: '789',
 *   payload: { ... } // AttorneyResponsePatchPayload
 * });
 */
export function usePatchAttorneyResponse() {
  return useMutation({
    mutationFn: ({
      invoiceId,
      appealId,
      timekeeperId,
      payload,
    }: {
      invoiceId: string;
      appealId: string;
      timekeeperId: string;
      payload: AttorneyResponsePatchPayload;
    }) => invoiceApi.patchAttorneyResponse(invoiceId, appealId, timekeeperId, payload),
    onSuccess: (_response, { payload }) => {
      const invoiceId = get(_response, 'invoiceId');
      const appealId = get(_response, 'appeal.appealId');
      queryClient.invalidateQueries({
        queryKey: ['attorneyResponse', invoiceId, appealId],
      });

      let message = `Attorney response submitted successfully!`;
      if (payload.status === AttorneyResponseStatus.EDIT_REQUESTED) {
        message = 'Attorney response revision requested successfully!';
      }
      showToast.success(message);
    },
    onError: (error: any) => {
      showToast.error(
        getApiErrorMessage(error, 'Failed to update attorney response. Please try again.')
      );
    },
  });
}

export function useGetAttorneyResponses(invoiceId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['attorneyResponses', invoiceId],
    queryFn: () => invoiceApi.getAttorneyResponses(invoiceId),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook for Invoice Search
 * Optimized for searching invoices by invoice number
 *
 * @param params - Search parameters (invoiceNumber, limit)
 * @param options - Optional configuration options
 * @returns A React Query result object containing invoice search results
 *
 * @example
 * const { data, isLoading } = useInvoiceSearch({ invoiceNumber: '1234', limit: 5 });
 */
export function useInvoiceSearch(params: InvoiceSearchParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['invoiceSearch', params] as const,
    queryFn: () => invoiceApi.searchInvoices(params),
    enabled: options?.enabled ?? true,
    staleTime: 0,
    gcTime: 0,
    retry: 1, // Fewer retries for invoice search
  });
}

/**
 * Hook for rolling back an invoice to a target status (PRE_APPEAL or AWAITING_APPEAL_SUBMISSION).
 *
 * @returns A React Query mutation object for rolling back the invoice.
 *
 * @example
 * const { mutateAsync: rollbackInvoice, isPending } = useRollbackInvoice();
 * await rollbackInvoice({ invoiceId: 'INV-1234', rollbackTarget: 'PRE_APPEAL', comment: 'Reason' });
 */
export function useRollbackInvoice() {
  const queryClientHook = useQueryClient();
  const { getQueryKey } = useQueryKeyStore();
  const matterInvoicesQueryKey = getQueryKey('ar-collections', 'queue.matter-invoices');
  const appealsQueryKey = getQueryKey('ar-collections', 'reporting.appeal');
  const appealByClientIdQueryKey = getQueryKey('ar-collections', 'reporting.appeal-by-client-id');

  return useMutation({
    mutationFn: (params: { invoiceId: string; rollbackTarget: string; comment: string }) =>
      invoiceApi.rollbackInvoice(params.invoiceId, params.rollbackTarget, params.comment),
    onSuccess: (response: any) => {
      const updatedInvoice = response ?? {};

      // Update matter invoices cache
      const matterInvoiceQuery = queryClientHook.getQueryData(matterInvoicesQueryKey) ?? {};
      const matterInvoices = get(matterInvoiceQuery, 'data', []) as MatterInvoice[];

      const invoiceIndex = findIndex(
        matterInvoices,
        invoice => invoice.invoiceId === updatedInvoice.invoiceId
      );
      if (invoiceIndex !== -1) {
        const currentInvoice = matterInvoices[invoiceIndex]!;
        const updatedInvoices = matterInvoices.map((inv, idx) =>
          idx === invoiceIndex
            ? {
                ...currentInvoice,
                appealStatus: updatedInvoice.appealStatus ?? AppealStatus.PRE_APPEAL,
                isCarrierInPreview: updatedInvoice.isCarrierInPreview ?? false,
                arBalance: updatedInvoice.arBalance ?? currentInvoice.outstandingBalance ?? 0,
                collectedAmount: get(updatedInvoice, 'appeal.collectedAmount', 0),
                writeOffAmount: updatedInvoice.writeOffAmount ?? 0,
                actualBalance: updatedInvoice.actualBalance ?? 0,
              }
            : inv
        );

        queryClientHook.setQueryData(matterInvoicesQueryKey, {
          ...matterInvoiceQuery,
          data: updatedInvoices,
        });
      }

      // Update appeals cache manually to reflect the rollback immediately.
      // We avoid triggering an immediate refetch from OpenSearch because
      // the index may not have been updated yet (indexing lag), which would
      // overwrite our correct cache with stale data.
      const rolledBackInvoice = updatedInvoice;
      const newStatus = rolledBackInvoice.appealStatus ?? AppealStatus.PRE_APPEAL;

      // Map DynamoDB response fields to the Appeal cache shape
      // (same mapping as reportingAppealsApi._mapper)
      const cacheUpdate: Record<string, any> = {
        status: newStatus,
        writeOffAmount: get(rolledBackInvoice, 'writeOffAmount', 0),
        actualBalance: get(rolledBackInvoice, 'actualBalance', 0),
        collectedAmount: get(rolledBackInvoice, 'appeal.collectedAmount', 0),
        remainingAmount: get(rolledBackInvoice, 'appeal.remainingAmount', 0),
        deadline: get(rolledBackInvoice, 'appeal.deadline'),
        attyDeadline: get(rolledBackInvoice, 'appeal.attyDeadline'),
        approvedWriteOffAmount: get(rolledBackInvoice, 'approvedWriteOffAmount', 0),
      };

      const updateAppealsCache = (queryKey: any) => {
        queryClientHook.setQueryData(queryKey, (oldData: any) => {
          if (!oldData) return oldData;

          const updateItem = (items: any[]) =>
            items.map((item: any) =>
              item.invoiceId === rolledBackInvoice.invoiceId
                ? { ...item, ...cacheUpdate }
                : item
            );

          if (Array.isArray(oldData)) {
            return updateItem(oldData);
          }

          if (oldData?.data && Array.isArray(oldData.data)) {
            return { ...oldData, data: updateItem(oldData.data) };
          }

          return oldData;
        });
      };

      // Update appeals cache and appeal-by-client-id cache
      if (!isEmpty(appealsQueryKey)) {
        updateAppealsCache(appealsQueryKey);
        // Mark as stale so it refetches on next navigation (but not now)
        queryClientHook.invalidateQueries({ queryKey: appealsQueryKey, refetchType: 'none' });
      }
      if (!isEmpty(appealByClientIdQueryKey)) {
        updateAppealsCache(appealByClientIdQueryKey);
        queryClientHook.invalidateQueries({ queryKey: appealByClientIdQueryKey, refetchType: 'none' });
      }

      // Mark all appeals/invoices queries as stale without immediate refetch
      queryClient.invalidateQueries({ queryKey: ['appeals'], refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });

      const newStatusForToast = updatedInvoice.appealStatus ?? AppealStatus.PRE_APPEAL;
      const toastMessage =
        newStatusForToast === AppealStatus.AWAITING_APPEAL_SUBMISSION
          ? 'Invoice rolled back to Awaiting Appeal Submission.'
          : 'Invoice rolled back to Pre-Appeal.';
      showToast.success(toastMessage);
    },
    onError: (error: any) => {
      const backendMessage = error?.response?.data?.message || error?.message || '';
      showToast.error(backendMessage || 'Failed to rollback invoice. Please try again.');
    },
  });
}

/**
 * Hook for updating the delegate for a specific timekeeper in an appeal.
 *
 * @returns A React Query mutation object for updating the delegate.
 *
 * @example
 * const { mutateAsync: updateDelegate, isPending } = useUpdateDelegate();
 * await updateDelegate({
 *   invoiceId: 'INV-1234',
 *   appealId: 'APL-5678',
 *   timekeeperId: 'TK-001',
 *   payload: { delegate: 'John Doe', delegateEmail: 'john@example.com' }
 * });
 */
export function useUpdateDelegate() {
  return useMutation({
    mutationFn: ({
      invoiceId,
      appealId,
      timekeeperId,
      payload,
    }: {
      invoiceId: string;
      appealId: string;
      timekeeperId: string;
      payload: DelegateUpdatePayload;
    }) => invoiceApi.updateDelegate(invoiceId, appealId, timekeeperId, payload),
    onSuccess: _response => {
      const invoiceId = get(_response, 'invoiceId');
      const appealId = get(_response, 'appeal.appealId');
      queryClient.invalidateQueries({
        queryKey: ['attorneyResponse', invoiceId, appealId],
      });
      showToast.success('Delegate updated successfully!');
    },
    onError: (error: any) => {
      showToast.error(
        getApiErrorMessage(error, 'Failed to update delegate. Please try again.')
      );
    },
  });
}

/**
 * Hook for bulk assigning or unassigning Appeals Clerk to/from invoices.
 *
 * @returns A React Query mutation object for bulk assignment.
 *
 * @example
 * const { mutateAsync: bulkAssign, isPending } = useBulkAssignAppealsClerk();
 * await bulkAssign({ invoiceIds: ['INV-1', 'INV-2'], assignedTo: 'clerk@example.com' });
 */
export function useBulkAssignAppealsClerk() {
  const queryClientHook = useQueryClient();
  const { getQueryKey } = useQueryKeyStore();

  return useMutation({
    mutationFn: (payload: BulkAssignAppealsClerkPayload) =>
      invoiceApi.bulkAssignAppealsClerk(payload),

    onSuccess: (_response, payload) => {
      const appealsQueryKey = getQueryKey('ar-collections', 'reporting.appeal');
      const appealByGroupIdQueryKey = getQueryKey('ar-collections', 'reporting.appeal-by-group-id');

      // Helper to update assignedTo for matching invoices
      const updateAssignedTo = (data: any[]) =>
        data.map(item =>
          payload.invoiceIds.includes(item.invoiceId)
            ? { ...item, assignedTo: payload.assignedTo || null }
            : item
        );

      // Update main appeals cache
      if (!isEmpty(appealsQueryKey)) {
        queryClientHook.setQueryData(appealsQueryKey, (oldData: any) => {
          if (!oldData) return oldData;

          // Paginated response
          if (oldData.data && Array.isArray(oldData.data)) {
            return {
              ...oldData,
              data: updateAssignedTo(oldData.data),
            };
          }

          // Simple array
          if (Array.isArray(oldData)) {
            return updateAssignedTo(oldData);
          }

          return oldData;
        });
      }

      // Update appeals by group ID cache (for client/attorney detail view)
      if (!isEmpty(appealByGroupIdQueryKey)) {
        queryClientHook.setQueryData(appealByGroupIdQueryKey, (oldData: any) => {
          if (!oldData) return oldData;

          // Paginated response
          if (oldData.data && Array.isArray(oldData.data)) {
            return {
              ...oldData,
              data: updateAssignedTo(oldData.data),
            };
          }

          // Simple array
          if (Array.isArray(oldData)) {
            return updateAssignedTo(oldData);
          }

          return oldData;
        });
      }
    },

    onError: (error: any) => {
      showToast.error(getApiErrorMessage(error, 'Failed to assign invoices. Please try again.'));
    },
  });
}
